import axios from 'axios';
import API_BASE_URL from '../config';
import { LAST_ACTIVITY_KEY } from '../models/session-inactivity.model';
import type { Admin, LoginResponse, RefreshResponse, ChangePasswordPayload, ForceChangePasswordPayload, AcceptInvitationPayload, InvitationVerifyResponse, PasswordResetRequestPayload, PasswordResetConfirmPayload, PasswordResetVerifyResponse } from '../types/api';

// El access token vive SOLO en memoria (no localStorage) para no exponerlo a
// XSS. Se rehidrata vía /auth/refresh (cookie httpOnly) al cargar la app.
let accessToken: string | null = null;

// El perfil del admin (no es credencial) sí persiste en localStorage para que
// getAdmin() siga siendo síncrono y los componentes no dependan del bootstrap.
const ADMIN_KEY = 'admin_user';

export const getAccessToken = (): string | null => accessToken;
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

const storeAdmin = (admin: Admin): void => {
  localStorage.setItem(ADMIN_KEY, JSON.stringify(admin));
};

const clearSession = (): void => {
  accessToken = null;
  localStorage.removeItem(ADMIN_KEY);
  // Borra el rastro de inactividad: si quedara una marca vieja, el siguiente
  // login la leería como sesión vencida y rebotaría al usuario a /login.
  localStorage.removeItem(LAST_ACTIVITY_KEY);
};

/**
 * Starts a session from a token the API issued: the login form's and the dev
 * quick-login's (task-dev-quick-login) go through here, so both behave alike.
 */
export const startSession = (token: string, admin: Admin): void => {
  accessToken = token;
  storeAdmin(admin);
  // El login es actividad explícita: abre una ventana de inactividad fresca.
  // Sin esto, un `session_last_activity` viejo (de una sesión previa rota)
  // haría que el guard de inactividad expire la sesión apenas montado.
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

export const login = async (username: string, password: string): Promise<LoginResponse> => {
  try {
    const response = await axios.post<LoginResponse>(
      `${API_BASE_URL}/auth/login`,
      { username, password },
      { withCredentials: true },
    );

    if (response.data.accessToken) {
      startSession(response.data.accessToken, response.data.admin);
      return response.data;
    }
    throw new Error('No se recibió token de acceso');
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.error || 'Error al iniciar sesión');
    }
    throw error;
  }
};

export const logout = async (): Promise<void> => {
  try {
    // Revoca la familia del refresh en el servidor (best-effort).
    await axios.post(`${API_BASE_URL}/auth/logout`, {}, { withCredentials: true });
  } catch {
    // Idempotente: aunque falle, limpiamos local igual.
  } finally {
    clearSession();
  }
};

export const getAdmin = (): Admin | null => {
  try {
    const adminStr = localStorage.getItem(ADMIN_KEY);
    return adminStr ? JSON.parse(adminStr) : null;
  } catch {
    localStorage.removeItem(ADMIN_KEY);
    return null;
  }
};

const doRefresh = async (): Promise<string> => {
  const response = await axios.post<RefreshResponse>(
    `${API_BASE_URL}/auth/refresh`,
    {},
    { withCredentials: true },
  );
  if (!response.data.accessToken) {
    throw new Error('Refresh sin token');
  }
  accessToken = response.data.accessToken;
  storeAdmin(response.data.admin);
  return accessToken;
};

// Single-flight global del refresh. TODOS los llamadores (interceptor de
// apiClient, bootstrapSession, InactivityGuard) comparten el mismo /refresh en
// vuelo. Sin esto, refresh concurrentes con la misma cookie hacen rotar la
// cadena de tokens varias generaciones; el backend lo interpreta como reúso
// (SEC C1) y revoca la familia → cierre de sesión. Coordinar acá lo evita.
let refreshPromise: Promise<string> | null = null;

/**
 * Llama a /auth/refresh y actualiza access token + admin en memoria/localStorage.
 * Coalesce: si ya hay un refresh en vuelo, devuelve la misma promesa.
 * Lanza si el refresh falla.
 */
export const refreshAccessToken = (): Promise<string> => {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Rehidrata la sesión al cargar la app (reemplaza al viejo verifyToken).
 * Reintenta una vez ante un 401: puede ser una carrera de pestañas donde otra
 * pestaña ya rotó la cookie de refresh (el 401 benigno es indistinguible del
 * de expiración por diseño — SEC M4/C1). Si el reintento también falla, no hay
 * sesión.
 */
export const bootstrapSession = async (): Promise<boolean> => {
  try {
    await refreshAccessToken();
    return true;
  } catch {
    await delay(400);
    try {
      await refreshAccessToken();
      return true;
    } catch {
      clearSession();
      return false;
    }
  }
};

export const isAuthenticated = (): boolean => !!accessToken;

export const getAuthHeaders = (): Record<string, string> => {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

export const changePassword = async (data: ChangePasswordPayload): Promise<void> => {
  await axios.post(`${API_BASE_URL}/auth/change-password`, data, {
    headers: getAuthHeaders(),
  });
};

export const forceChangePassword = async (data: ForceChangePasswordPayload): Promise<Admin> => {
  const response = await axios.post(`${API_BASE_URL}/auth/force-change-password`, data, {
    headers: getAuthHeaders(),
  });
  if (response.data.admin) {
    localStorage.setItem(ADMIN_KEY, JSON.stringify(response.data.admin));
  }
  return response.data.admin;
};

export const verifyInvitation = async (token: string): Promise<InvitationVerifyResponse> => {
  try {
    const response = await axios.get<InvitationVerifyResponse>(`${API_BASE_URL}/auth/invitation/verify`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data as InvitationVerifyResponse;
    }
    return { valid: false, error: 'Error al verificar la invitación' };
  }
};

export const acceptInvitation = async (data: AcceptInvitationPayload): Promise<void> => {
  await axios.post(`${API_BASE_URL}/auth/invitation/accept`, data);
};

export const requestPasswordReset = async (data: PasswordResetRequestPayload): Promise<{ message: string }> => {
  const response = await axios.post<{ message: string }>(`${API_BASE_URL}/auth/password-reset/request`, data);
  return response.data;
};

export const verifyPasswordResetToken = async (token: string): Promise<PasswordResetVerifyResponse> => {
  try {
    const response = await axios.get<PasswordResetVerifyResponse>(`${API_BASE_URL}/auth/password-reset/verify`, {
      params: { token },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      return error.response.data as PasswordResetVerifyResponse;
    }
    return { valid: false, error: 'Error al verificar el token' };
  }
};

export const confirmPasswordReset = async (data: PasswordResetConfirmPayload): Promise<void> => {
  await axios.post(`${API_BASE_URL}/auth/password-reset/confirm`, data);
};
